import { RecommendedContactSummary } from './contact';
import { GetRecommendedContactRequest } from './contact.request';
import { EmbeddedCode } from '../shared';
import { Entity } from '../entity';

/**
 * Groups for organizing contractor recommendations based on their match quality
 */
export enum RecommendationGroup {
  /** In zone: matches both work category and the job's zone. Capacity and price are carried as row properties, not separate groups. */
  Recommended = 'Recommended',
  /** In zone, but pricing could not produce a rankable price: no single pricebook match, or lines missing from the matched book. Only assigned while price-based recommendations are on. */
  NotRanked = 'NotRanked',
  /** In category but not in the job's zone */
  OutOfZone = 'OutOfZone',
  /** Matches neither work category nor zone — hidden in the enhanced dialog */
  Other = 'Other'
}

/**
 * Extended recommendation summary with capacity and pricing information
 */
export interface EnhancedRecommendedContactSummary extends RecommendedContactSummary {
  /** The recommendation group this contractor falls into */
  group: RecommendationGroup;
  /** Remaining capacity (null = unlimited or not applicable). Cleared for users without CrmSecurityRight.ContractorsEdit — read overCapacity instead of deriving from this. */
  capacityRemaining: number | null;
  /** Total capacity configured (null = unlimited). Cleared for users without CrmSecurityRight.ContractorsEdit. */
  capacityTotal: number | null;
  /**
   * Whether the contractor is at or over their capacity threshold. Derived server-side from capacityRemaining before
   * the counts are withheld, so the selection gate and the 'No capacity' label keep working for users who may not
   * see the raw figures.
   */
  overCapacity: boolean;
  /** Calculated buy price (null = price not calculated or no match) */
  buyPrice: number | null;
  /** How the pricebook was matched. 'not-priced' = pricing was not attempted (out of zone, or pricing disabled) */
  priceBookMatch: 'single' | 'multiple' | 'none' | 'not-priced';
  /** The pricebook(s) that matched - single match, multiple conflicts, or empty if none */
  matchedPricebooks: Entity[];
  /**
   * Distinct contractor-supplied codes the matched pricebook could not resolve (no site row, static row, dynamic rule or
   * non-zero catalogue cost). Any entry means the buy price is incomplete and the row is Not Ranked; the dialog lists
   * them so the gap can be fixed. Null when pricing was not attempted, no single book matched, or the pricing service
   * was unavailable.
   */
  unpricedCodes: string[] | null;
  /**
   * Price rank within the Recommended group, 1-based, ties sharing a rank (1, 1, 3). Null for Out of Zone
   * (never priced), for unpriced rows, and when the tenant has price-based recommendations off.
   * Assigned server-side before buyPrice is withheld, so it survives for users without ViewPricing.
   */
  rank: number | null;
}

/**
 * Request for enhanced contractor recommendations with job context for pricing
 */
export interface GetEnhancedRecommendedContactRequest extends GetRecommendedContactRequest {
  /** Job ID - placeholder for future use, not currently used. Could be used to auto-lookup coverage parameters from job. */
  jobId?: string;
  /** Site ID for site-specific pricing */
  siteId?: string;
  /** Job type ID for pricebook coverage */
  jobTypeId?: string;
  /** Priority ID for pricebook coverage */
  priorityId?: string;
  /** Customer ID for pricebook coverage */
  customerId?: string;
  /** Billing company ID for pricebook coverage */
  billingCompanyId?: string;
  /** Stage codes to calculate buy prices for */
  stageCodes?: EmbeddedCode[];
}

/**
 * User permissions related to contractor recommendations
 */
export interface UserRecommendationPermissions {
  /** Whether user can select contractors over their capacity threshold */
  canOverrideCapacity: boolean;
  /** Whether user holds JobSecurityRight.AllowOutOfZoneSelection. When false the Out of Zone group is hidden and only in-zone contractors can be picked. */
  allowOutOfZoneSelection: boolean;
  /** Whether user holds JobSecurityRight.ViewPricing. When false the server clears buyPrice and the dialog hides the price column; ordering is unchanged. */
  canViewPricing: boolean;
  /** Whether user holds CrmSecurityRight.ContractorsEdit. When false the dialog hides raw capacity counts; the over-capacity state and its selection gate still apply. */
  canViewCapacity: boolean;
}

/**
 * Full response from enhanced recommendation endpoint
 */
export interface EnhancedRecommendationResponse {
  /** List of recommended contractors with enhanced data */
  contractors: EnhancedRecommendedContactSummary[];
  /** Current user's permissions for selection */
  userPermissions: UserRecommendationPermissions;
}
